import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { CATEGORIES } from "../constants";
import { auth } from "../lib/firebase";
import {
  findUnit, effectiveMorale, effectiveComposition, effectiveSpecialRules,
  moraleLabel, formatComposition, lineTotal, lineBrPerCopy,
  unitEquipment, parseAmmoRow, equipmentName, findEquipment,
  ordersFormula, officerModifier, scoutingTotal,
} from "../lib/army";

const INK = "#111";
const MUTED = "#666";
const LINE = "#ccc";

const ruleName = (r) => (typeof r === "string" ? r : r.name);
const movementText = (eq) => (eq.movement || []).map((m) => `${m.cat} ${m.move}`).join(", ");
function armourText(armour) {
  if (!armour) return "";
  if (typeof armour === "string") return armour;
  const a = Array.isArray(armour) ? armour[0] : armour;
  if (!a) return "";
  return `Front ${a.Front} / Side ${a.Side} / Rear ${a.Rear}`;
}
function weaponsList(weapons) {
  if (!weapons || !weapons.length) return [];
  return weapons.map((w) => {
    if (typeof w === "string") return w;
    const bits = [];
    if (w.mount) bits.push(`Mount: ${w.mount}`);
    if (w.range != null && w.range !== "") bits.push(`Range: ${w.range}`);
    if (w.rof) bits.push(`RoF: ${w.rof}`);
    const detail = bits.length ? ` ${bits.join(", ")}` : "";
    return (w.weapon || "Weapon") + detail;
  }).filter(Boolean);
}

const s = StyleSheet.create({
  page: { paddingTop: 76, paddingBottom: 30, paddingHorizontal: 24, fontFamily: "Helvetica", color: INK },

  header: { position: "absolute", top: 14, left: 24, right: 24, height: 50,
    flexDirection: "row", alignItems: "center",
    borderBottomWidth: 2.5, borderBottomColor: INK, paddingBottom: 6 },
  logo: { width: 40, height: 40, objectFit: "contain", marginRight: 12 },
  headerMain: { flexGrow: 1 },
  title: { fontFamily: "Helvetica-Bold", fontSize: 19, textTransform: "uppercase", letterSpacing: 0.3 },
  sub: { fontFamily: "Courier", fontSize: 8, color: MUTED, marginTop: 2, textTransform: "uppercase", letterSpacing: 0.5 },
  headerStats: { flexDirection: "column", alignItems: "flex-end", justifyContent: "center" },
  statLine: { fontFamily: "Courier", fontSize: 11, fontWeight: "bold", textAlign: "right", marginVertical: 1 },

  trackerWrap: { marginBottom: 14 },
  rhSub: { fontFamily: "Courier", fontSize: 8, color: MUTED, marginTop: 3, textTransform: "uppercase", letterSpacing: 0.5 },
  subRow: { flexDirection: "row", alignItems: "baseline", width: "100%" },
  createdBy: { flexGrow: 1, flexBasis: 0, textAlign: "center", fontFamily: "Courier", fontSize: 7, color: MUTED, marginTop: 3 },
  trackerRow: { flexDirection: "row", alignItems: "stretch", marginBottom: 14 },
  trackerCol: { flexGrow: 1, flexShrink: 1, paddingRight: 18 },
  ordersCol: { width: 170, borderLeftWidth: 1, borderLeftColor: INK, paddingLeft: 18, justifyContent: "center", flexShrink: 0 },
  ordersBox: { borderWidth: 1, borderColor: INK, paddingVertical: 6, paddingHorizontal: 10, width: "100%" },
  ordersTitle: { fontFamily: "Helvetica-Bold", fontSize: 13, letterSpacing: 0.6 },
  ordersFormula: { fontFamily: "Courier-Bold", fontSize: 12, marginTop: 3 },
  ordersTicks: { flexDirection: "row", flexWrap: "wrap", marginTop: 5 },
  ordersTick: { width: 12, height: 12, borderWidth: 0.75, borderColor: INK, marginRight: 3, marginBottom: 3 },
  ordersScouting: { fontFamily: "Courier", fontSize: 8.5, marginTop: 6 },
  trackerLabel: { fontFamily: "Helvetica-Bold", fontSize: 9, marginBottom: 4 },
  tracker: { flexDirection: "row", flexWrap: "wrap" },
  box: { width: 16, height: 16, marginRight: 2, marginBottom: 2, borderWidth: 0.75, borderColor: INK,
    alignItems: "center", justifyContent: "center" },
  boxTxt: { fontFamily: "Courier", fontSize: 6.5 },
  boxCur: { backgroundColor: "#c62828", borderColor: "#c62828" },
  boxCurTxt: { color: "#fff", fontFamily: "Courier-Bold", fontSize: 8 },

  section: { marginTop: 10 },
  secHead: { backgroundColor: INK, flexDirection: "row", justifyContent: "space-between",
    alignItems: "center", paddingVertical: 4, paddingHorizontal: 10 },
  secName: { color: "#fff", fontFamily: "Helvetica-Bold", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8 },
  secNums: { color: "#fff", fontFamily: "Courier", fontSize: 9 },

  unit: { borderWidth: 1, borderColor: INK, paddingVertical: 5, paddingHorizontal: 9, marginBottom: 3 },
  unitLast: { marginBottom: 12 },
  unitHead: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" },
  unitName: { fontFamily: "Helvetica-Bold", fontSize: 11, flexShrink: 1, paddingRight: 8 },
  chips: { flexDirection: "row", gap: 4 },
  chip: { fontFamily: "Courier", fontSize: 7, borderWidth: 0.75, borderColor: INK, paddingVertical: 1, paddingHorizontal: 4 },
  chipDark: { backgroundColor: INK, color: "#fff", borderColor: INK },
  line: { fontSize: 8.5, color: "#222", marginTop: 2 },
  lbl: { fontFamily: "Courier", fontSize: 7, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 },

  eqWrap: { marginTop: 5, paddingLeft: 8, borderLeftWidth: 1.5, borderLeftColor: LINE },
  eq: { marginBottom: 4 },
  eqName: { fontFamily: "Helvetica-Bold", fontSize: 9 },
  eqType: { fontFamily: "Courier", fontSize: 6.5, color: "#888", textTransform: "uppercase" },
  eqLine: { fontSize: 8 },
  wpnLine: { fontSize: 8, marginLeft: 12 },
  eqNested: { marginTop: 3, paddingLeft: 8, borderLeftWidth: 0.75, borderLeftColor: "#bbb" },

  amRow: { flexDirection: "row" },
  amCell: { borderWidth: 0.5, borderColor: "#999", paddingVertical: 1.5, paddingHorizontal: 3,
    fontFamily: "Courier", fontSize: 7, textAlign: "center" },
  amHead: { backgroundColor: "#e8e8e8", fontSize: 6.5, textTransform: "uppercase" },
  amWpn: { textAlign: "left", fontFamily: "Helvetica-Bold", fontSize: 7.5 },
});

const AM_W = { wpn: 92, ammo: 50, he: 54, range: 28 };
const SECTION_KEEP = 110;
const AM_COLS = ["0-20", "20-30", "30-40", "40-50", "50-70"];

function AmmoTable({ eq }) {
  const rows = [];
  (eq.ammo || []).forEach((entry) => {
    Object.keys(entry).forEach((type) => rows.push(parseAmmoRow(type, entry[type])));
  });
  if (!rows.length) return null;
  return (
    <View style={{ marginTop: 3 }}>
      <View style={s.amRow}>
        <Text style={[s.amCell, s.amHead, { width: AM_W.wpn, textAlign: "left" }]}>Weapon</Text>
        <Text style={[s.amCell, s.amHead, { width: AM_W.ammo }]}>Ammo</Text>
        <Text style={[s.amCell, s.amHead, { width: AM_W.he }]}>HE</Text>
        {AM_COLS.map((c) => <Text key={c} style={[s.amCell, s.amHead, { width: AM_W.range }]}>{c}</Text>)}
      </View>
      {rows.map((r, i) => (
        <View style={s.amRow} key={i}>
          <Text style={[s.amCell, s.amWpn, { width: AM_W.wpn }]}>{i === 0 ? equipmentName(eq) : ""}</Text>
          <Text style={[s.amCell, { width: AM_W.ammo }]}>{r.type}</Text>
          <Text style={[s.amCell, { width: AM_W.he }]}>{r.he}</Text>
          {r.ranges.map((v, j) => <Text key={j} style={[s.amCell, { width: AM_W.range }]}>{v}</Text>)}
        </View>
      ))}
    </View>
  );
}

function EquipmentStats({ eq, faction }) {
  if (eq.ammo) {
    return (
      <View style={s.eq}>
        <Text><Text style={s.eqName}>{equipmentName(eq)} </Text><Text style={s.eqType}>weapon</Text></Text>
        <AmmoTable eq={eq} />
      </View>
    );
  }
  const mv = movementText(eq);
  const wpns = weaponsList(eq.weapons);
  const rules = (eq.specialRules || []).join(", ");
  const showArmour = eq.type === "vehicle" || eq.type === "helicopter";
  const showHits = eq.type === "aircraft" || eq.type === "infantry";

  const seen = new Set();
  const nested = [];
  (eq.weapons || []).forEach((w) => {
    if (w && typeof w === "object" && w.equipmentId && !seen.has(w.equipmentId)) {
      seen.add(w.equipmentId);
      const wEq = findEquipment(faction, w.equipmentId);
      if (wEq) nested.push(wEq);
    }
  });

  return (
    <View style={s.eq}>
      <Text><Text style={s.eqName}>{equipmentName(eq)} </Text><Text style={s.eqType}>{eq.type}</Text></Text>
      {mv ? <Text style={s.eqLine}><Text style={s.lbl}>Move </Text>{mv}</Text> : null}
      {showArmour && armourText(eq.armour) ? <Text style={s.eqLine}><Text style={s.lbl}>Armour </Text>{armourText(eq.armour)}</Text> : null}
      {showHits && eq.hits != null ? <Text style={s.eqLine}><Text style={s.lbl}>Hits </Text>{eq.hits}</Text> : null}
      {wpns.length > 0 ? (
        <View>
          <Text style={s.eqLine}><Text style={s.lbl}>Weapons</Text></Text>
          {wpns.map((line, i) => <Text key={i} style={s.wpnLine}>{line}</Text>)}
        </View>
      ) : null}
      {(eq.type === "vehicle" || eq.type === "helicopter") && rules ? <Text style={s.eqLine}><Text style={s.lbl}>Special </Text>{rules}</Text> : null}
      {nested.length > 0 && (
        <View style={s.eqNested}>
          {nested.map((wEq) => <EquipmentStats key={wEq.equipmentId} eq={wEq} faction={faction} />)}
        </View>
      )}
    </View>
  );
}

function PrintUnit({ faction, entry, last, minPresenceAhead }) {
  const unit = findUnit(faction, entry.unitId);
  if (!unit) return null;
  const morale = effectiveMorale(unit, entry);
  const comp = formatComposition(effectiveComposition(unit, entry));
  const rules = effectiveSpecialRules(unit, entry).map(ruleName).filter(Boolean);
  const br = lineBrPerCopy(unit, entry) * entry.qty;
  const equipment = unitEquipment(faction, unit, entry);

  return (
    <View wrap={false} minPresenceAhead={minPresenceAhead} style={[s.unit, last && s.unitLast]}>
      <View style={s.unitHead}>
        <Text style={s.unitName}>{entry.qty}× {unit.name}</Text>
        <View style={s.chips}>
          {morale ? <Text style={s.chip}>{moraleLabel(morale)}</Text> : null}
          <Text style={[s.chip, s.chipDark]}>{lineTotal(faction, entry)} pts</Text>
          <Text style={s.chip}>BR {br}</Text>
        </View>
      </View>
      {comp ? <Text style={s.line}><Text style={s.lbl}>Composition: </Text>{comp}</Text> : null}
      {rules.length > 0 ? <Text style={s.line}><Text style={s.lbl}>Special rules: </Text>{rules.join(", ")}</Text> : null}
      {equipment.length > 0 && (
        <View style={s.eqWrap}>
          {equipment.map((eq) => <EquipmentStats key={eq.equipmentId} eq={eq} faction={faction} />)}
        </View>
      )}
    </View>
  );
}

export default function RosterPdfDocument({ faction, roster, limit, totals }) {
  const totalBr = totals.br;
  const brBoxes = Array.from({ length: Math.max(0, totalBr) }, (_, i) => i + 1);
  const orders = faction ? ordersFormula(faction, roster, limit) : "";
  const ordersMod = faction ? officerModifier(faction, roster) : 0;
  const scouting = faction ? scoutingTotal(faction, roster) : 0;
  const createdByEmail = (auth.currentUser && auth.currentUser.email) || "";

  return (
    <Document title={faction ? `${faction.name} — Roster` : "Roster"}>
      <Page size="A4" style={s.page}>
        <View fixed style={s.header}>
          {faction && faction.factionLogo ? <Image style={s.logo} src={faction.factionLogo} /> : null}
          <View style={s.headerMain}>
            <Text style={s.title}>{faction ? faction.name : "No army"}</Text>
            {faction ? (
              <View style={s.subRow}>
                <Text style={s.rhSub}>{faction.group || ""}</Text>
                <Text style={s.createdBy}>(List created by: {createdByEmail})</Text>
              </View>
            ) : null}
          </View>
          <View style={s.headerStats}>
            <Text style={s.statLine}>Points: {totals.pts} / {limit || "\u2014"}</Text>
            <Text style={s.statLine}>BR: {totalBr}</Text>
          </View>
        </View>

        {faction && (
          <View style={s.trackerRow}>
            <View style={s.trackerCol}>
              {totalBr > 0 && (
                <>
                  <Text style={s.trackerLabel}>Battle Rating Tracker:</Text>
                  <View style={s.tracker}>
                    {brBoxes.map((n) => {
                      const cur = n === totalBr;
                      return (
                        <View key={n} style={[s.box, cur && s.boxCur]}>
                          <Text style={cur ? s.boxCurTxt : s.boxTxt}>{n}</Text>
                        </View>
                      );
                    })}
                  </View>
                </>
              )}
            </View>
            <View style={s.ordersCol}>
              <View style={s.ordersBox}>
                <Text style={s.ordersTitle}>ORDERS</Text>
                <Text style={s.ordersFormula}>{orders}</Text>
                {ordersMod > 0 && (
                  <View style={s.ordersTicks}>
                    {Array.from({ length: ordersMod }).map((_, i) => (
                      <View key={i} style={s.ordersTick} />
                    ))}
                  </View>
                )}
                <Text style={s.ordersScouting}>Scouting: {scouting}</Text>
              </View>
            </View>
          </View>
        )}

        {faction && CATEGORIES
          .map((cat) => {
            const entries = roster.filter((e) => {
              const u = findUnit(faction, e.unitId);
              return u && u.category === cat;
            });
            if (entries.length === 0) return null;
            const secPts = entries.reduce((acc, e) => acc + lineTotal(faction, e), 0);
            const secBr = entries.reduce((acc, e) => {
              const u = findUnit(faction, e.unitId);
              return acc + (u ? lineBrPerCopy(u, e) * e.qty : 0);
            }, 0);
            return { cat, entries, secPts, secBr };
          })
          .filter(Boolean)
          .flatMap((sec) => [
            // Zero-height guard: if less than SECTION_KEEP points of space remain
            // on the page, break here so the following section (its `fixed`
            // repeating header + first unit) starts on the next page instead of
            // the header being orphaned at the bottom of this page.
            <View key={sec.cat + "-keep"} minPresenceAhead={SECTION_KEEP} />,
            <View key={sec.cat} style={s.section} wrap>
              <View style={s.secHead} fixed>
                <Text style={s.secName}>{sec.cat}</Text>
                <Text style={s.secNums}>{sec.secPts} pts | BR: {sec.secBr}</Text>
              </View>
              {sec.entries.map((entry, i) => (
                <PrintUnit key={entry.unitId} faction={faction} entry={entry} last={i === sec.entries.length - 1} />
              ))}
            </View>,
          ])}
      </Page>
    </Document>
  );
}
